import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNotificationTemplateVariables, useNotificationTemplates } from "services/hooks/rentals";
import {
  EVENT_KEY_OPTIONS,
  EVENT_KEY_TO_TEMPLATE_TYPE,
  channelsForEventKey,
  deliveryModeForEventKey,
} from "../constants";

const VARIABLE_GROUP_LABELS = {
  tenant_due: "Tenant due variables",
  landlord_payout: "Landlord payout variables",
  landlord_tenant_self_onboard: "Landlord tenant self-onboard",
  tenant_payment_receipt: "Tenant payment receipt",
  tenant_broadcast: "Tenant broadcast",
  landlord_broadcast: "Landlord broadcast",
};

const getEventTemplatePayload = (form) => ({
  related_organization: form.related_organization ? Number(form.related_organization) : null,
  name: form.name.trim(),
  channel: form.channel,
  subject: form.subject ?? "",
  body: form.body,
  is_active: form.is_active,
  event_key: form.event_key,
  delivery_mode: form.delivery_mode,
});

const EventTemplatesTab = () => {
  const {
    templates,
    isLoadingTemplates,
    errorTemplates,
    isSubmittingTemplate,
    templateActionError,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useNotificationTemplates();

  const eventTemplates = useMemo(() => templates.filter((row) => Boolean(row.event_key)), [templates]);

  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const bodyInputRef = useRef(null);
  const [form, setForm] = useState({
    related_organization: "",
    name: "",
    event_key: EVENT_KEY_OPTIONS[0]?.value || "",
    delivery_mode: deliveryModeForEventKey(EVENT_KEY_OPTIONS[0]?.value),
    channel: channelsForEventKey(EVENT_KEY_OPTIONS[0]?.value)[0] || "EMAIL",
    subject: "",
    body: "",
    is_active: true,
  });

  const templateType = EVENT_KEY_TO_TEMPLATE_TYPE[form.event_key] || "";
  const {
    templateVariableGroups,
    isLoadingTemplateVariables,
    templateVariablesError,
    fetchTemplateVariables,
  } = useNotificationTemplateVariables(templateType);

  const channelOptions = useMemo(() => channelsForEventKey(form.event_key), [form.event_key]);

  const handleEventKeyChange = (nextEventKey) => {
    const nextDeliveryMode = deliveryModeForEventKey(nextEventKey);
    const nextChannels = channelsForEventKey(nextEventKey);
    setForm((prev) => ({
      ...prev,
      event_key: nextEventKey,
      delivery_mode: nextDeliveryMode,
      channel: nextChannels.includes(prev.channel) ? prev.channel : nextChannels[0] || "EMAIL",
    }));
    fetchTemplateVariables(EVENT_KEY_TO_TEMPLATE_TYPE[nextEventKey]);
  };

  const handleOpenCreate = () => {
    const defaultEventKey = EVENT_KEY_OPTIONS[0]?.value || "";
    setEditingId(null);
    setForm({
      related_organization: "",
      name: "",
      event_key: defaultEventKey,
      delivery_mode: deliveryModeForEventKey(defaultEventKey),
      channel: channelsForEventKey(defaultEventKey)[0] || "EMAIL",
      subject: "",
      body: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      related_organization: row.related_organization ?? "",
      name: row.name || "",
      event_key: row.event_key || EVENT_KEY_OPTIONS[0]?.value || "",
      delivery_mode: row.delivery_mode || deliveryModeForEventKey(row.event_key),
      channel: row.channel || "EMAIL",
      subject: row.subject || "",
      body: row.body || "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim() || !form.event_key) {
      setMessage({ type: "error", text: "Name, event, and body are required." });
      return;
    }
    if (!channelOptions.includes(form.channel)) {
      setMessage({ type: "error", text: "Channel is not allowed for this event type." });
      return;
    }
    const payload = getEventTemplatePayload(form);
    const response = editingId ? await updateTemplate(editingId, payload) : await createTemplate(payload);
    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? "Event template updated." : "Event template created.",
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || "Failed to save event template." });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event template?")) return;
    const response = await deleteTemplate(id);
    if (response.success) {
      setMessage({ type: "success", text: "Event template deleted." });
    } else {
      setMessage({ type: "error", text: response.message || "Failed to delete event template." });
    }
  };

  const insertTokenInBody = (token) => {
    const input = bodyInputRef.current;
    const currentBody = form.body || "";
    if (!input || typeof input.selectionStart !== "number" || typeof input.selectionEnd !== "number") {
      setForm((prev) => ({ ...prev, body: `${prev.body || ""}${token}` }));
      return;
    }
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const nextBody = `${currentBody.slice(0, start)}${token}${currentBody.slice(end)}`;
    const nextCursor = start + token.length;
    setForm((prev) => ({ ...prev, body: nextBody }));
    setTimeout(() => {
      if (bodyInputRef.current) {
        bodyInputRef.current.focus();
        bodyInputRef.current.setSelectionRange(nextCursor, nextCursor);
      }
    }, 0);
  };

  const variableGroupEntries = Object.entries(templateVariableGroups);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Event Templates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Add Event Template
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One-off sends triggered by events (not daily cron rules). Scheduled tenant/payout rules must use templates
        without an event key — see the Templates tab.
      </Typography>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}
      {errorTemplates && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load templates.
        </Alert>
      )}
      {templateActionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {templateActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingTemplates ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eventTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No event templates found.</TableCell>
                </TableRow>
              ) : (
                eventTemplates.map((row) => {
                  const eventMeta = EVENT_KEY_OPTIONS.find((option) => option.value === row.event_key);
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.name || "—"}</TableCell>
                      <TableCell>{eventMeta?.label || row.event_key || "—"}</TableCell>
                      <TableCell>{row.delivery_mode || "—"}</TableCell>
                      <TableCell>{row.channel || "—"}</TableCell>
                      <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit Event Template" : "Create Event Template"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Template Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Event</InputLabel>
            <Select
              label="Event"
              value={form.event_key}
              onChange={(event) => handleEventKeyChange(event.target.value)}
            >
              {EVENT_KEY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Delivery mode"
            value={form.delivery_mode}
            InputProps={{ readOnly: true }}
            helperText="Set automatically from the selected event."
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={form.channel}
              onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))}
            >
              {channelOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Subject"
            value={form.subject}
            onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            minRows={4}
            multiline
            label="Body"
            value={form.body}
            inputRef={bodyInputRef}
            onChange={(event) => setForm((prev) => ({ ...prev, body: event.target.value }))}
          />

          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Placeholder tokens ({templateType || "event"})
            </Typography>
            {isLoadingTemplateVariables ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading variables…</Typography>
              </Box>
            ) : variableGroupEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No variables returned for this event type.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {variableGroupEntries.map(([groupKey, variables]) => (
                  <Box key={groupKey}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {VARIABLE_GROUP_LABELS[groupKey] || groupKey}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {variables.map((variable) => (
                        <Chip
                          key={variable.key}
                          label={variable.token}
                          title={variable.description || variable.key}
                          onClick={() => insertTokenInBody(variable.token)}
                          variant="outlined"
                          clickable
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
            {templateVariablesError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {templateVariablesError}
              </Alert>
            )}
          </Box>

          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingTemplate}>
            {isSubmittingTemplate ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventTemplatesTab;
